import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@dashboard/utils/format-currency";
import { PlatformCountry } from "@dashboard/common/types";
import {
  DeliveryProviderQuote,
  OrderCalculation,
} from "../../entities/delivery.entities";
import {
  formatDeliveryAddress,
  formatDeliveryPickupDate,
} from "../../utils/delivery-create.utils";
import { DeliveryCheckoutSummaryWrapper } from "./delivery-checkout-summary.styled";
import MapPin from "@sumerkit/icons/map-pin";
import User from "@sumerkit/icons/user";
import DeviceMobile from "@sumerkit/icons/device-mobile";
import Envelope from "@sumerkit/icons/envelope";
import Package from "@sumerkit/icons/package";
import Truck from "@sumerkit/icons/truck";
import CalendarBlank from "@sumerkit/icons/calendar-blank";
import TShirt from "@sumerkit/icons/t-shirt";
import { Clipboard, CurrencyDollar } from "@phosphor-icons/react";

type DeliveryCheckoutSummaryProps = {
  calculation: OrderCalculation;
  selectedProvider: DeliveryProviderQuote | null;
  sizeLabel: string;
  typeLabel: string;
  linkedOrderId: number | null;
  userCountry?: PlatformCountry;
  variant?: "checkout" | "detail";
};

type InfoRowProps = {
  icon: ReactNode;
  label: string;
  value: string;
  secondary?: string;
};

const InfoRow: FC<InfoRowProps> = ({ icon, label, value, secondary }) => {
  if (!value || value === "—") return null;

  return (
    <div className="info-row">
      <span className="row-icon">{icon}</span>
      <div className="row-content">
        <p className="row-label">{label}</p>
        <p className="row-value">{value}</p>
        {secondary && <p className="row-value-secondary">{secondary}</p>}
      </div>
    </div>
  );
};

const SectionHeader: FC<{
  icon: ReactNode;
  title: string;
  subtitle?: string;
}> = ({ icon, title, subtitle }) => (
  <div className="summary-section-header">
    <span className="section-icon">{icon}</span>
    <div className="summary-section-header-text">
      <h3>{title}</h3>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
    </div>
  </div>
);

export const DeliveryCheckoutSummary: FC<DeliveryCheckoutSummaryProps> = ({
  calculation,
  selectedProvider,
  sizeLabel,
  typeLabel,
  linkedOrderId,
  userCountry,
  variant = "checkout",
}) => {
  const { t, i18n } = useTranslation();
  const { user, customer } = calculation;
  const provider = selectedProvider?.provider;
  const total = selectedProvider?.total ?? calculation.totalValue;
  const locale = userCountry?.locale || i18n.language;
  const isDetail = variant === "detail";

  const productsCount = calculation.products?.filter(
    (p) => p.name && p.name !== "Envío"
  ).length;

  const orderDetailText = isDetail
    ? productsCount && productsCount > 0
      ? t("delivery.checkout.productsCount", { count: productsCount })
      : customer.observations?.trim() ||
        calculation.observations?.trim() ||
        ""
    : productsCount && productsCount > 0
      ? t("delivery.checkout.productsCount", { count: productsCount })
      : customer.observations?.trim() ||
        calculation.observations?.trim() ||
        "";

  const orderDetailLabel = t("delivery.checkout.orderDetail");

  const pickupDateFormatted = formatDeliveryPickupDate(
    calculation.pickupDate,
    locale
  );

  const originAddress = formatDeliveryAddress({
    address: user.address,
    addressDetails: user.addressDetails,
    city: user.city,
  });

  const destinationAddress = formatDeliveryAddress({
    address: customer.address,
    addressDetails: customer.addressDetails,
    city: customer.city,
  });

  const customerPhone = [customer.prefixPhone, customer.phone]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <DeliveryCheckoutSummaryWrapper>
      {linkedOrderId && (
        <span className="linked-order-badge">
          {t("delivery.checkout.linkedOrder", { orderId: linkedOrderId })}
        </span>
      )}

      <section className="summary-section">
        <SectionHeader
          icon={<MapPin weight="light" />}
          title={t("delivery.checkout.addressesTitle")}
          subtitle={t("delivery.checkout.addressesSubtitle")}
        />
        <div className="address-route">
          <div className="address-route-line">
            <span className="dot" />
            <span className="connector" />
            <span className="dot" />
          </div>
          <div className="address-cards">
            <div className="address-card">
              <p className="address-card-label">
                {t("delivery.checkout.originAddress")}
              </p>
              <p className="address-card-value">{originAddress}</p>
            </div>
            <div className="address-card">
              <p className="address-card-label">
                {t("delivery.checkout.destinationAddress")}
              </p>
              <p className="address-card-value">{destinationAddress}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="summary-section">
        <SectionHeader
          icon={<Clipboard weight="light" />}
          title={t("delivery.checkout.informationTitle")}
        />
        <InfoRow
          icon={<User weight="light" />}
          label={t("delivery.checkout.clientName")}
          value={customer.name}
        />
        <InfoRow
          icon={<DeviceMobile weight="light" />}
          label={t("delivery.checkout.clientPhone")}
          value={customerPhone}
        />
        {!isDetail && (
          <InfoRow
            icon={<Envelope weight="light" />}
            label={t("delivery.create.customerEmail")}
            value={customer.email?.trim() || ""}
          />
        )}
        <InfoRow
          icon={<TShirt weight="light" />}
          label={orderDetailLabel}
          value={orderDetailText}
        />
        <InfoRow
          icon={<Package weight="light" />}
          label={t("delivery.create.deliverySize")}
          value={sizeLabel || calculation.deliverySize || ""}
        />
        <InfoRow
          icon={<Truck weight="light" />}
          label={t("delivery.create.deliveryType")}
          value={typeLabel}
        />
        {!isDetail && (
          <InfoRow
            icon={<Truck weight="light" />}
            label={t("delivery.create.provider")}
            value={provider?.name || calculation.provider || ""}
            secondary={provider?.descriptionTime}
          />
        )}
        <InfoRow
          icon={<CalendarBlank weight="light" />}
          label={t("delivery.create.pickupDate")}
          value={pickupDateFormatted}
        />
      </section>

      <section className="summary-section">
        <SectionHeader
          icon={<CurrencyDollar weight="light" />}
          title={t("delivery.checkout.costTitle")}
        />
        <div className="cost-rows">
          <div className="cost-row">
            <span className="cost-label">{t("delivery.checkout.subtotal")}</span>
            <span className="cost-value">
              {formatCurrency(total, userCountry)}
            </span>
          </div>
          <div className="cost-row total">
            <span className="cost-label">{t("delivery.checkout.total")}</span>
            <span className="cost-value">
              {formatCurrency(total, userCountry)}
            </span>
          </div>
        </div>
      </section>
    </DeliveryCheckoutSummaryWrapper>
  );
};
